import { c as _c } from "react/compiler-runtime";
import { ContextCurrencyFragment, currencyToAssetIdentifier, readCurrencyFragment } from "@opensea/app-kit";
import { graphql, useClient } from "@opensea/graphql";
import { bn } from "@opensea/utils";
import { delay } from "es-toolkit";
import { useState } from "react";
const FETCH_INTERVAL = 500;
export const SWAP_RECEIPT_QUERY = graphql(`
    query useSwapReceipt(
      $transactionIdentifiers: [TransactionIdentifierSwapProvider!]!
      $swapQuote: SwapAssetQuoteInput!
    ) {
      swapReceipt(
        transactionIdentifiers: $transactionIdentifiers
        swapQuote: $swapQuote
      ) {
        status
        failReason
        crossChainRefunded
        assetReceipts {
          fromAsset {
            __typename
            ... on Currency {
              ...ContextCurrency
            }
          }
          fromQuantity
          toAsset {
            __typename
            ... on Currency {
              ...ContextCurrency
            }
          }
          toQuantity
        }
        failedAssetReceipts {
          fromAsset {
            ... on Currency {
              ...ContextCurrency
            }
          }
          fromQuantity
          toAsset {
            ... on Currency {
              ...ContextCurrency
            }
          }
          toQuantity
        }
      }
    }
  `, [ContextCurrencyFragment]);
export const useSwapReceipt = t0 => {
  const $ = _c(14);
  const {
    fromCurrency,
    toCurrency,
    fromQuantity,
    toQuantity,
    swapProvider
  } = t0;
  const [swapReceipt, setSwapReceipt] = useState(undefined);
  const client = useClient();
  let t1;
  if ($[0] !== client || $[1] !== fromCurrency || $[2] !== fromQuantity || $[3] !== swapProvider || $[4] !== toCurrency || $[5] !== toQuantity) {
    t1 = async transactionIdentifier => {
      const hasRequiredFields = fromQuantity && toQuantity && swapProvider;
      if (!hasRequiredFields) {
        throw new Error("Missing required fields to fetch swap receipt");
      }
      const {
        data
      } = await client.query(SWAP_RECEIPT_QUERY, {
        transactionIdentifiers: transactionIdentifier ? [{
          swapProvider: swapProvider ?? "UNRECOGNIZED",
          transactionIdentifier: {
            chain: transactionIdentifier.chain,
            transactionHash: transactionIdentifier.hash
          }
        }] : [],
        swapQuote: {
          fromAssets: [{
            asset: currencyToAssetIdentifier(fromCurrency),
            quantity: fromQuantity?.toString() ?? "0"
          }],
          toAssets: [{
            asset: currencyToAssetIdentifier(toCurrency ?? {}),
            quantity: toQuantity.toString()
          }]
        }
      });
      if (!data?.swapReceipt) {
        return;
      }
      const assetReceipt = data.swapReceipt.assetReceipts.at(0) ?? data.swapReceipt.failedAssetReceipts.at(0);
      if (!assetReceipt) {
        throw new Error("Expected asset receipt or failed asset receipt");
      }
      if (assetReceipt.fromAsset.__typename !== "Currency") {
        throw new Error("From asset is not a currency");
      }
      if (assetReceipt.toAsset.__typename !== "Currency") {
        throw new Error("To asset is not a currency");
      }
      const fromAsset = readCurrencyFragment(assetReceipt.fromAsset);
      const toAsset = readCurrencyFragment(assetReceipt.toAsset);
      return {
        status: data.swapReceipt.status,
        fromAsset,
        fromQuantity: bn(assetReceipt.fromQuantity, fromAsset.decimals).toNumber(),
        toAsset,
        toQuantity: bn(assetReceipt.toQuantity, toAsset.decimals).toNumber(),
        transactionIdentifier,
        swapProvider: swapProvider ?? "UNRECOGNIZED",
        failReason: data.swapReceipt.failReason ?? undefined,
        crossChainRefunded: data.swapReceipt.crossChainRefunded ?? false
      };
    };
    $[0] = client;
    $[1] = fromCurrency;
    $[2] = fromQuantity;
    $[3] = swapProvider;
    $[4] = toCurrency;
    $[5] = toQuantity;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const fetchReceipt = t1;
  let pollForReceipt;
  if ($[7] !== fetchReceipt) {
    pollForReceipt = async transactionIdentifier_0 => {
      const receipt = await fetchReceipt(transactionIdentifier_0);
      if (receipt && receipt.status !== "PENDING") {
        return receipt;
      }
      await delay(FETCH_INTERVAL);
      return pollForReceipt(transactionIdentifier_0);
    };
    $[7] = fetchReceipt;
    $[8] = pollForReceipt;
  } else {
    pollForReceipt = $[8];
  }
  let t2;
  if ($[9] !== pollForReceipt) {
    t2 = async transactionIdentifier_1 => {
      const receipt_0 = await pollForReceipt(transactionIdentifier_1);
      setSwapReceipt(receipt_0);
      return receipt_0;
    };
    $[9] = pollForReceipt;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const waitForCompletion = t2;
  let t3;
  if ($[11] !== swapReceipt || $[12] !== waitForCompletion) {
    t3 = [swapReceipt, waitForCompletion];
    $[11] = swapReceipt;
    $[12] = waitForCompletion;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};